import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikWrapper, FormRadioButtons } from '@uncinc/uncinc-react-kitchen-sink';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { getRegistrationData } from '../../../store/actions/Registrations';
import { userUpdate } from '../../../store/actions/User';
import Page from '../../../containers/Page';
import { Button, Main } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useToast from '../../../hooks/useToast';

import './style.scss';

interface Props {
  className?: string;
}

const PageAccountAvatar: React.FC<Props> = ({
  className,
}) => {
  const { buildClass, joinClasses } = useClasses('PageAccountAvatar');
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const operationStatus = useOperationStatus('user', 'update');
  const toast = useToast();

  const currentUserId = useSelector<ImmutableRootState<number>, number>(
    (state) => state.getIn(['login', 'uid'], 0),
  );
  const fieldAvatar = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['user', currentUserId, 'field_avatar', 0, 'value'], 1));

  const onSubmit = async (values) => {
    await dispatch(userUpdate(currentUserId, {
      field_avatar: [{
        value: values.avatar.slice(8),
      }],
    })).then(() => {
      dispatch(getRegistrationData());
      toast.show(t('pages.account.avatar.success'), 'success');
    }).catch((error) => {
      toast.show(t('pages.account.avatar.error'), 'error');
      console.error(error);
    });
  };

  const validationSchema = Yup.object().shape({
    avatar: Yup.string().required(t('pages.account.avatar.fields.avatar.validation.required')),
  });

  const options = {
    'avatar--1': 1,
    'avatar--2': 2,
    'avatar--3': 3,
    'avatar--4': 4,
    'avatar--5': 5,
    'avatar--6': 6,
    'avatar--7': 7,
    'avatar--8': 8,
    'avatar--9': 9,
    'avatar--10': 10,
    'avatar--11': 11,
    'avatar--12': 12,
  };

  return (
    <Page className={joinClasses(buildClass(), className)} title={t('pages.account.avatar.title')}>
      <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <h1 className={buildClass('title')}>{t('pages.account.avatar.title')}</h1>
        <p className={buildClass('body')}>{t('pages.account.avatar.body')}</p>
        <FormikWrapper
          initialValues={{ avatar: `avatar--${fieldAvatar}` }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await onSubmit(values);
            setSubmitting(false);
          }}
        >
          <FormRadioButtons
            fieldName="avatar"
            options={options}
          />
          <Button
            htmlType="submit"
            buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
          >
            {t('buttons.submit')}
          </Button>
        </FormikWrapper>
      </Main>
    </Page>
  );
};

export default PageAccountAvatar;
